'use client';

import {
  Box,
  Divider,
  GridLayout,
  SystemIcon,
  Typography,
} from '@wishket/design-system';
import { ChangeEvent, FormEvent, useRef, useEffect } from 'react';

import { EmptyRecentSearches, RecentSearchItem } from './Search.parts';

interface SearchDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onClickDeleteAllRecentSearchItemsButton?: () => void;
  onClickDeleteRecentSearchItemButton: (index: number) => void;
  recentSearchList?: string[];
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

const SearchBox = ({
  isOpen,
  onClose,
  query,
  onQueryChange,
  onClickDeleteAllRecentSearchItemsButton,
  onClickDeleteRecentSearchItemButton,
  recentSearchList,
  onSubmit,
}: SearchDrawerProps) => {
  const isEmpty = !recentSearchList || recentSearchList.length === 0;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <GridLayout>
        <Box className="flex h-16 items-center gap-3 whitespace-nowrap grid-in-content">
          <SystemIcon name="large_search" />
          <form className="flex-1" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              type="text"
              placeholder="키워드, 작가 이름 검색하기"
              className="h-[26px] w-full pt-px text-w-gray-600 outline-none placeholder:text-w-gray-300 placeholder:typo-body1"
              value={query}
              onChange={onQueryChange}
            />
          </form>
          <button onClick={onClose}>
            <SystemIcon name="large_delete" />
          </button>
        </Box>
      </GridLayout>
      <Divider />
      <GridLayout>
        <Box className="flex min-h-[352px] flex-col gap-8 pb-12 pt-8 grid-in-content desktop:gap-6 desktop:pb-[60px] desktop:pt-12">
          <Box className="flex justify-between">
            <Typography variant="body1" className="font-medium text-w-gray-900">
              최근 검색어
            </Typography>
            {!isEmpty && (
              <Box
                className="flex cursor-pointer items-center gap-2"
                onClick={onClickDeleteAllRecentSearchItemsButton}
              >
                <SystemIcon
                  name="medium_trash"
                  className="shrink-0 text-w-gray-600"
                />
                <Typography variant="body1" className="text-w-gray-600">
                  전체 삭제
                </Typography>
              </Box>
            )}
          </Box>
          {isEmpty ? (
            <EmptyRecentSearches />
          ) : (
            <Box className="flex flex-col-reverse gap-4">
              {recentSearchList.map((recentSearch, index) => (
                <RecentSearchItem
                  key={index}
                  recentSearch={recentSearch}
                  onClickDeleteRecentSearchItemButton={() =>
                    onClickDeleteRecentSearchItemButton(index)
                  }
                  onClose={onClose}
                />
              ))}
            </Box>
          )}
        </Box>
      </GridLayout>
    </>
  );
};

export default SearchBox;
